import React from 'react';
import { observer } from 'mobx-react-lite';
import { Avatar, Box, Button, Flex, Skeleton, Text } from '@radix-ui/themes';
import { toast } from 'sonner';

import { useUserProfile } from '../user-profile.provider';

const DEFAULT_AVATAR = '/Images/jobseekerSide/dummy-profile.png';

const AvatarUploaderView = () => {
   const userProfile = useUserProfile();
   const inputId = React.useId();
   const [validationError, setValidationError] = React.useState<string | null>(null);

   React.useEffect(() => {
      return () => {
         userProfile.resetSelectedPhoto();
      };
   }, [userProfile]);

   const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
         return;
      }

      const result = await userProfile.selectPhotoFile(file);
      if (result.error) {
         setValidationError(result.error);
      } else {
         setValidationError(null);
      }

      e.target.value = '';
   };

   const handleSave = async () => {
      const result = await userProfile.savePhoto();
      if (result.ok) {
         toast.success(result.message);
         return;
      }

      toast.error(result.message);
   };

   return (
      <Box>
         <Text as="div" size="3" mb="3" weight="medium">
             Фото профілю
         </Text>

         <Flex
            gap="4"
            align={{ initial: 'start', sm: 'center' }}
            direction={{ initial: 'column', sm: 'row' }}
         >
            <Skeleton loading={userProfile.avatarSyncLoading}>
               <Avatar
                  size="9"
                  radius="full"
                  src={userProfile.avatarPreviewImage ?? DEFAULT_AVATAR}
                  fallback="P"
               />
            </Skeleton>

            <Flex direction="column" gap="2">
               <input
                  id={inputId}
                  type="file"
                  hidden
                  accept=".jpeg,.jpg,.png"
                  onChange={handleChange}
               />

               <Flex gap="2" wrap="wrap">
                  <Button asChild variant="soft" color="indigo">
                     <label htmlFor={inputId}>Виберіть фото</label>
                  </Button>

                  <Button
                     color="red"
                     onClick={handleSave}
                     loading={userProfile.uploadPhotoState.status === 'pending'}
                     disabled={!userProfile.hasPendingPhotoUpload}
                  >
                     Зберегти фото
                  </Button>

                  <Button
                     color="gray"
                     variant="outline"
                     disabled={!userProfile.hasPendingPhotoUpload}
                     onClick={() => {
                        userProfile.resetSelectedPhoto();
                        setValidationError(null);
                     }}
                  >
                     Скасувати
                  </Button>
               </Flex>

               <Text size="1" color={validationError ? 'red' : 'gray'}>
                  {validationError ?? 'Підтримувані типи файлів: gif, jpg, jpeg, png (макс. 500 KB). Мін розмір файлу 250 х 250 пікселів.'}
               </Text>
            </Flex>
         </Flex>
      </Box>
   );
};

export const AvatarUploader = observer(AvatarUploaderView);
